import React, { useMemo, useState } from 'react';
import { getRandomIndexFlashcard } from '@osmosis/shared/quiz';
import { useFetch } from '../../../../context/FetchProvider';
import { useQuizContext } from '../../../../context/QuizContext';
import QuizSubmitButtons from '../../QuizSubmitButtons';
import styles from './Flashcard.module.css';
import { FlashcardData, QuizConfidence } from '../../../../types/quiz';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Box } from '@osmosis/react-ui/components/Box';
import { P } from '@osmosis/react-ui/components/Text';
import { renderPrompt, renderAnswered } from './utils';
import FlashcardControls from '../flashcard-controls/FlashcardControls';
import { QuizContainer } from '../../QuizContainer';
import { SkipQuizButton } from '../utils';
import { setCookie } from 'cookies-next';
import { QUIZ_CONFIDENCE_POPUP_KEY } from '../../../../constants';
import { RateConfidenceWindow } from '../utils/rate-confidence/RateConfidenceWindow';
import { QuizSettings } from '../../../../lib/shared/quiz/quizSetting';
import DoNotRepeatButton from './DoNotRepeatButton';
import FlagButton from './FlagButton';
import { FormattedMessage, useIntl } from 'react-intl';
import Divider from '../../../Divider';
import { faFlag } from '@fortawesome/pro-regular-svg-icons/faFlag';
import { faFlag as faFlagSolid } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { SpacedRepetitionState } from '../../../../types/queues';
import LearnifyText from '../../../learn/LearnifyText';
import { getRepetitionStats } from '../../../../lib/frontend/quiz';
import { useQuery } from 'react-query';
import {
  textGray780,
  textNegativeText,
  textPrimary,
} from '@osmosis/tailwind-ui/styles/text';

type Props = {
  queueType?: string;
};
/**
 * Flashcard component for quiz.
 */
const Flashcard = ({ queueType }: Props) => {
  const intl = useIntl();
  const {
    confidencePopupHidden,
    setConfidencePopupHidden,
    currentItem,
    quizSettings,
    isAnswered,
    setShowFlashcardBack,
    currentItemMetadata: metadata,
    handleFlag,
    handleReschedule,
    handleUnschedule,
  } = useQuizContext();
  const { front, flashcardType } = currentItem?.data as FlashcardData;

  const { get } = useFetch();
  const defaultStats = { due: {}, intervals: [] };
  const { data: repetitionStats = defaultStats } = useQuery(
    'quiz-repetition-stats',
    async () => await getRepetitionStats(get, currentItem?.nodeID ?? 0),
    { initialData: defaultStats },
  );

  const [answerVisible, setAnswerVisible] = useState(false);
  const [confidence, setConfidence] = useState<QuizConfidence>(
    QuizConfidence.NA,
  );

  const showAnswer = isAnswered || answerVisible;
  const showLearnifyLinks = true;

  const randomIndex = useMemo(
    () => getRandomIndexFlashcard(front, flashcardType),
    [front, flashcardType],
  );

  const hiddenHtml = renderPrompt({
    text: front,
    type: flashcardType,
    randomIndex,
    className: styles.blank,
  });

  const showHtml = renderAnswered({
    text: front,
    type: flashcardType,
    randomIndex,
    className: styles.animate,
  });

  const handleSubmitConfidence = (confidence: QuizConfidence) => {
    setShowFlashcardBack(true);
    setAnswerVisible(true);
    setConfidence(confidence);
  };

  const hideConfidencePopup = () => {
    setConfidencePopupHidden(true);
    setCookie(QUIZ_CONFIDENCE_POPUP_KEY, true);
  };

  return (
    <Box width={'100%'}>
      <Flex flexDirection={'column'} alignContent={'center'}>
        <QuizContainer
          minHeight={200}
          settingsHeading={intl.formatMessage({
            defaultMessage: 'Flashcard settings',
            id: 'quiz.settings.flashcard_settings',
          })}
          settingItems={[
            {
              id: 'heading',
              component: (
                <P ml={3} pb={2} opacity={0.5}>
                  <FormattedMessage
                    defaultMessage="Flashcard repetition interval"
                    id="quiz.settings.repetition_settings_heading"
                  />
                </P>
              ),
            },
            {
              id: 'repeat-in-1-minute',
              text: intl.formatMessage({
                defaultMessage: 'Repeat in 1 minute',
                id: 'quiz.settings.repeat_in_1_minute',
              }),
              onClick: () => handleReschedule(1),
            },
            {
              id: 'repeat-in-10-minutes',
              text: intl.formatMessage({
                defaultMessage: 'Repeat in 10 minutes',
                id: 'quiz.settings.repeat_in_10_minutes',
              }),
              onClick: () => handleReschedule(10),
            },
            {
              id: 'repeat-in-1-day',
              text: intl.formatMessage({
                defaultMessage: 'Repeat in 1 day',
                id: 'quiz.settings.repeat_in_1_day',
              }),
              onClick: () => handleReschedule(60 * 24),
            },
            {
              id: 'no-repeat',
              text: intl.formatMessage({
                defaultMessage: 'Never repeat',
                id: 'quiz.settings.never_repeat',
              }),
              textColor: textNegativeText,
              onClick: handleUnschedule,
            },
            {
              id: 'divider-1',
              component: <Divider cn="my-2" />,
            },
            {
              id: 'flag-as-priority',
              text: metadata?.flagged
                ? intl.formatMessage({
                    defaultMessage: 'Unflag as priority',
                    id: 'quiz.settings.unflag_as_priority',
                  })
                : intl.formatMessage({
                    defaultMessage: 'Flag as priority',
                    id: 'quiz.settings.flag_as_priority',
                  }),
              icon: metadata?.flagged ? faFlagSolid : faFlag,
              iconColor: metadata?.flagged ? textPrimary : textGray780,
              onClick: handleFlag,
            },
            {
              id: 'divider-2',
              component: <Divider cn="my-2" />,
            },
          ]}
          headerItems={
            <>
              {quizSettings[QuizSettings.FLASHCARDS_FLAG_VISIBLE] && (
                <FlagButton
                  onClick={handleFlag}
                  data-testid="quiz.flashcard.flag"
                  flagged={!!metadata?.flagged}
                />
              )}

              {quizSettings[QuizSettings.FLASHCARDS_NO_REPEAT] && (
                <DoNotRepeatButton
                  data-testid="quiz.flashcard.do-not-repeat"
                  onClick={handleUnschedule}
                  active={metadata?.state === SpacedRepetitionState.UNSCHEDULED}
                />
              )}
            </>
          }
        >
          <Box sx={{ color: 'gray60' }}>
            <LearnifyText
              data-testid="quiz.Flashcard.answer"
              text={showAnswer ? showHtml : hiddenHtml}
              enabled={showLearnifyLinks}
            />
          </Box>
        </QuizContainer>
        <Flex flexDirection="column">
          {!isAnswered && (
            <Flex flexDirection="column" sx={{ position: 'relative' }}>
              {answerVisible ? (
                <FlashcardControls
                  repetitionStats={repetitionStats}
                  confidence={confidence}
                />
              ) : (
                <>
                  <QuizSubmitButtons submit={handleSubmitConfidence} />
                  {!confidencePopupHidden && (
                    <RateConfidenceWindow hidePopup={hideConfidencePopup} />
                  )}
                </>
              )}
            </Flex>
          )}
        </Flex>
        {/* not showing skip button for flashcard queue*/}
        {queueType !== 'flashcards' && <SkipQuizButton />}
      </Flex>
    </Box>
  );
};

export default Flashcard;
